import { useState, useEffect } from 'react';
import { Clock, MousePointer2, Share2, Swords, RotateCcw, User, Home, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Run, Match, MODE_LABELS, DIFFICULTY_LABELS, CLICK_LIMITS } from '@/types/game';
import { formatTime } from '@/lib/utils';
import { PathBreadcrumb } from './PathBreadcrumb';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

interface ResultCardProps {
  run: Run;
  match: Match;
  onPlayAgain: () => void;
  onGoHome: () => void;
  onBeatThisRun?: () => void;
  onGoToProfile?: () => void;
  compareRun?: Run | null;
}

export function ResultCard({
  run,
  match,
  onPlayAgain,
  onGoHome,
  onBeatThisRun,
  onGoToProfile,
  compareRun,
}: ResultCardProps) {
  const [revealStage, setRevealStage] = useState(0);
  const isSuccess = run.completed;
  const clickLimit = match.mode === 'under_clicks' ? CLICK_LIMITS[match.difficulty] : null;
  const isUnderLimit = clickLimit ? run.clicks <= clickLimit : true;
  const isWin = isSuccess && isUnderLimit;

  // Staggered reveal effect
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    timers.push(setTimeout(() => setRevealStage(1), 100)); // Header
    timers.push(setTimeout(() => setRevealStage(2), 250)); // Route
    timers.push(setTimeout(() => setRevealStage(3), 400)); // Stats
    timers.push(setTimeout(() => setRevealStage(4), 550)); // Path
    timers.push(setTimeout(() => setRevealStage(5), 700)); // Actions
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleShare = async () => {
    const shareUrl = run.id ? `${window.location.origin}/run/${run.id}` : window.location.origin;
    const shareText = isWin
      ? `I went from "${match.start_title}" to "${match.target_title}" in ${run.clicks} clicks and ${formatTime(run.duration_ms || 0)}. Can you beat me?`
      : `I tried to go from "${match.start_title}" to "${match.target_title}" but didn't make it. Try it yourself!`;

    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="game-card max-w-md mx-auto">
      {/* Result header */}
      <div className={`text-center mb-6 transition-all duration-300 ${
        revealStage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        {isWin ? (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-success">Victory!</h2>
            <p className="text-muted-foreground text-sm mt-1">You found the path</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <X className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-destructive">
              {isSuccess && !isUnderLimit ? 'Over Limit' : 'Game Over'}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {isSuccess && !isUnderLimit
                ? `Needed ${clickLimit} clicks or fewer`
                : 'Better luck next time'}
            </p>
          </>
        )}
      </div>

      {/* Route info */}
      <div className={`bg-background/50 rounded-lg p-4 mb-4 transition-all duration-300 ${
        revealStage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground uppercase tracking-wider">Route</span>
          <span className={`mode-badge ${match.mode === 'time_attack' ? 'time-attack' : 'under-clicks'}`}>
            {MODE_LABELS[match.mode]} · {DIFFICULTY_LABELS[match.difficulty]}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className="truncate">{match.start_title}</span>
          <span className="text-primary shrink-0">→</span>
          <span className="text-primary truncate">{match.target_title}</span>
        </div>
      </div>

      {/* Stats */}
      <div className={`grid grid-cols-2 gap-3 mb-6 transition-all duration-300 ${
        revealStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        <div className="stat-block">
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-4 h-4 text-accent" />
            <span className="stat-value text-accent">{formatTime(run.duration_ms || 0)}</span>
          </div>
          <p className="stat-label">Time</p>
          {compareRun && (
            <p className={`text-xs mt-1 ${
              (run.duration_ms || 0) < (compareRun.duration_ms || 0) ? 'text-success' : 'text-destructive'
            }`}>
              vs {formatTime(compareRun.duration_ms || 0)}
            </p>
          )}
        </div>
        <div className="stat-block">
          <div className="flex items-center justify-center gap-2">
            <MousePointer2 className={`w-4 h-4 ${isUnderLimit ? 'text-primary' : 'text-destructive'}`} />
            <span className={`stat-value ${isUnderLimit ? 'text-primary' : 'text-destructive'}`}>
              {run.clicks}
              {clickLimit && <span className="text-lg text-muted-foreground">/{clickLimit}</span>}
            </span>
          </div>
          <p className="stat-label">Clicks</p>
          {compareRun && (
            <p className={`text-xs mt-1 ${
              run.clicks < compareRun.clicks ? 'text-success' : 'text-destructive'
            }`}>
              vs {compareRun.clicks}
            </p>
          )}
        </div>
      </div>

      {/* Path preview */}
      {run.path && run.path.length > 0 && (
        <div className={`bg-background/50 rounded-lg p-3 mb-6 transition-all duration-300 ${
          revealStage >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Your path</p>
          <PathBreadcrumb path={run.path} maxVisible={4} />
        </div>
      )}

      {/* Actions */}
      <div className={`space-y-3 transition-all duration-300 ${
        revealStage >= 5 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}>
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={handleShare} variant="outline" size="sm" className="gap-2">
            <Share2 className="w-4 h-4" />
            Share
          </Button>
          {onBeatThisRun && (
            <Button
              onClick={() => {
                trackEvent.beatThisRunClicked(run.id);
                onBeatThisRun();
              }}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Swords className="w-4 h-4" />
              Challenge
            </Button>
          )}
        </div>
        <Button onClick={onPlayAgain} className="w-full gap-2">
          <RotateCcw className="w-4 h-4" />
          Play Again
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={onGoHome}
            variant="ghost"
            className="gap-2 text-muted-foreground"
          >
            <Home className="w-4 h-4" />
            Home
          </Button>
          {onGoToProfile && (
            <Button
              onClick={onGoToProfile}
              variant="ghost"
              className="gap-2 text-muted-foreground"
            >
              <User className="w-4 h-4" />
              Profile
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
